import { makeAutoObservable } from "mobx";
import { SCOPE_KEYS } from "../domains/scope";
import {
  isDimension,
  normalizeEntityId,
  otherDimension,
} from "../utils/e-charts-dimensions";

const MAX_YEAR_RANGE = 30;
const EMPTY_DATA = { rows: [], warnings: [] };
const DEFAULTS = {
  groupBy: "bu",
  drillContext: null,
  yearRangeStart: null,
  yearRangeEnd: null,
  availableYears: [],
  selectedYear: null,
  selectedYearIsManual: false,
  autoSelectedYearContextKey: "",
  autoAppliedRangeKey: "",
  consolidated: false,
};

// A dimension is unrestricted ("all") until the user narrows it. "all" is an
// EXPLICIT mode, never inferred from an empty id list and never from ids that
// happen to equal the full option list: the request builder must OMIT the
// `within` filter for "all" so organizations with no country tag keep counting
// towards their business unit, and an id-list comparison cannot express that
// difference (R09 item 5 brief 3.2 item 1).
//
// "none" is the third explicit mode (Brief D, YY 2026-08-09, superseding her
// earlier at-least-one rule): nothing selected, no request sent, the chart
// explains itself. INVARIANT: `ids` is ALWAYS an array in every mode; readers
// iterate `selection.ids` unconditionally.
const allSelection = () => ({ mode: "all", ids: [] });
const noneSelection = () => ({ mode: "none", ids: [] });

const uniqueIds = (values) => [
  ...new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => (value == null || value === "" ? null : String(value)))
      .filter(Boolean),
  ),
];

class DashboardEChartsStore {
  groupBy = DEFAULTS.groupBy;
  selectionByDimension = { bu: allSelection(), country: allSelection() };
  drillContext = DEFAULTS.drillContext;
  yearRangeStart = DEFAULTS.yearRangeStart;
  yearRangeEnd = DEFAULTS.yearRangeEnd;
  availableYears = [...DEFAULTS.availableYears];
  selectedYear = DEFAULTS.selectedYear;
  selectedYearIsManual = DEFAULTS.selectedYearIsManual;
  autoSelectedYearContextKey = DEFAULTS.autoSelectedYearContextKey;
  autoAppliedRangeKey = DEFAULTS.autoAppliedRangeKey;
  consolidated = DEFAULTS.consolidated;

  data = EMPTY_DATA;
  // Body of the request the current data came from. The empty-state and the
  // displayed-entity derivation both need to know whether `within` was sent,
  // and which entities it named, without re-deriving it from live state that
  // may already have moved on.
  lastRequest = null;
  // Fingerprint of the request context the current data answers. The panel
  // compares it against the ambient context so rows from an older selection
  // are never presented as the current filtered result while a refetch is
  // pending (Codex round 3 finding 1).
  dataFingerprint = null;
  // View context the current rows answer, captured when their request was
  // built: displayed dimension, that dimension's selection, drill (with the
  // display name), and years. While a refetch is pending the panel keeps the
  // previous charts mounted AS the previous view, so titles and entity lists
  // must come from here and never from live state that already moved on.
  dataContext = null;
  // The last matching-context request failed and nothing newer is scheduled.
  // Rendered as an explicit error state with a retry, never as stale rows.
  loadError = false;
  loading = false;
  hasLoadedOnce = false;
  requestSeq = 0;

  constructor() {
    makeAutoObservable(this);
  }

  nextRequestSeq = () => {
    this.requestSeq += 1;
    return this.requestSeq;
  };

  isStaleSeq = (seq) => seq !== this.requestSeq;

  // The group-by toggle only changes which dimension the bars enumerate, so
  // BOTH selections survive it. It does exit a drill: the drill was expressed
  // in terms of the old grouping and has no meaning under the new one.
  setGroupBy = (next) => {
    if (!isDimension(next)) return;
    if (next === this.groupBy) return;
    this.groupBy = next;
    this.drillContext = null;
  };

  selectionFor = (dimension) =>
    isDimension(dimension) ? this.selectionByDimension[dimension] : null;

  // Narrow one dimension. Removing the last item (or unticking All) lands in
  // the explicit none mode, following the ESG data table convention (YY,
  // LINE 2026-08-09 16:08, reversing her earlier at-least-one rule). The ops
  // layer refuses to fetch for a none selection and the panel renders the
  // select-at-least-one description instead of a chart.
  setEntityIds = (dimension, values) => {
    if (!isDimension(dimension)) return;
    const ids = uniqueIds(values);
    this.selectionByDimension[dimension] =
      ids.length === 0 ? noneSelection() : { mode: "subset", ids };
  };

  // Explicit All. Distinct from "every option ticked": All omits `within`.
  setAllEntities = (dimension) => {
    if (!isDimension(dimension)) return;
    this.selectionByDimension[dimension] = allSelection();
  };

  setAvailableYears = (values) => {
    this.availableYears = [
      ...new Set(values.map(Number).filter(Number.isFinite)),
    ]
      .slice()
      .sort((a, b) => a - b);
  };

  setYearRange = ({ start, end }) => {
    const nextStart = start == null || start === "" ? null : Number(start);
    const nextEnd = end == null || end === "" ? null : Number(end);
    this.yearRangeStart = Number.isFinite(nextStart) ? nextStart : null;
    this.yearRangeEnd = Number.isFinite(nextEnd) ? nextEnd : null;
  };

  setYearRangeStart = (value) => {
    this.setYearRange({ start: value, end: this.yearRangeEnd });
  };

  setYearRangeEnd = (value) => {
    this.setYearRange({ start: this.yearRangeStart, end: value });
  };

  // User-driven strip selection (including explicit "None"). Marks the
  // selection manual so the auto-default never overrides it — the flag is
  // store state, not a component ref, so it survives tab unmount.
  setSelectedYear = (value) => {
    const n = value == null || value === "" ? null : Number(value);
    this.selectedYear = Number.isFinite(n) ? n : null;
    this.selectedYearIsManual = true;
  };

  // Auto-default for the strip year; no-op once the user picked one.
  // Sticky within a filter context: an existing auto selection is kept while
  // the totals filter context is unchanged and the year is still a boundary
  // year, so trend-range changes never move the strip selection (the strip
  // fetches out-of-range years for itself). The default re-applies when the
  // context rotates (frameworks / reporting periods changed, data genuinely
  // differs) or when the selected year leaves the boundary.
  applyDefaultSelectedYear = (value, contextKey = "") => {
    if (this.selectedYearIsManual) return;
    const sameContext =
      contextKey !== "" && contextKey === this.autoSelectedYearContextKey;
    const currentStillValid =
      Number.isFinite(this.selectedYear) &&
      this.availableYears.includes(this.selectedYear);
    if (sameContext && currentStillValid) return;
    const n = Number(value);
    this.selectedYear = Number.isFinite(n) ? n : null;
    this.autoSelectedYearContextKey = contextKey;
  };

  // Auto-apply the default trend range unless the active range diverged
  // from the last auto-applied one (user or seed changed it).
  applyDefaultYearRange = ({ start, end }) => {
    const nextKey = `${start ?? ""}|${end ?? ""}`;
    const activeKey = `${this.yearRangeStart ?? ""}|${this.yearRangeEnd ?? ""}`;
    if (activeKey !== "|" && activeKey !== this.autoAppliedRangeKey) return;
    this.setYearRange({ start, end });
    this.autoAppliedRangeKey = nextKey;
  };

  // Seed from the top FilterBar ("seed and diverge"): adopt the year as the
  // strip selection and widen the trend range to include it. Widen only — a
  // single-year top pick must never collapse the multi-year range.
  seedSelectedYear = (value) => {
    const year = Number(value);
    if (!Number.isFinite(year)) return;
    // No membership check against availableYears: that cache refreshes only
    // while the charts tab is mounted, and the top picker's vocabulary is the
    // same reporting boundary, so a rejection here could only be a stale one.
    if (Number.isFinite(this.yearRangeStart) && year < this.yearRangeStart) {
      this.yearRangeStart = year;
    }
    if (Number.isFinite(this.yearRangeEnd) && year > this.yearRangeEnd) {
      this.yearRangeEnd = year;
      // activeYears keeps the FIRST MAX_YEAR_RANGE years of the range, so
      // widening the end past the cap would silently drop the year we are
      // selecting. Pull the start up until the seeded year fits.
      const span = this.availableYears.length
        ? this.availableYears.filter(
            (y) => y >= this.yearRangeStart && y <= year,
          )
        : null;
      if (span && span.length > MAX_YEAR_RANGE) {
        this.yearRangeStart = span[span.length - MAX_YEAR_RANGE];
      } else if (!span && year - this.yearRangeStart + 1 > MAX_YEAR_RANGE) {
        this.yearRangeStart = year - (MAX_YEAR_RANGE - 1);
      }
    }
    this.selectedYear = year;
    this.selectedYearIsManual = true;
  };

  // Seed from the top FilterBar organization pick. A seed is an explicit new
  // context for the dimension it names, so it narrows that dimension and
  // exits any drill; the OTHER dimension is left exactly as the user had it
  // (a top-bar seed narrows one dimension, it never un-narrows the other).
  seedEntities = (groupBy, ids) => {
    if (!isDimension(groupBy)) return;
    const next = uniqueIds(ids);
    if (next.length === 0) return;
    this.groupBy = groupBy;
    this.selectionByDimension[groupBy] = { mode: "subset", ids: next };
    this.drillContext = null;
  };

  // Drill into one bar of the hero chart. Guarded to the per-entity view
  // (consolidated has no per-entity bars) and to one level (an existing drill
  // blocks a second). The drilled dimension's own selection is deliberately
  // left untouched, which is what makes breadcrumb-back restore the exact
  // prior view with no saved-state machinery.
  drillInto = (entityId, entityName) => {
    if (this.consolidated || this.drillContext) return;
    const id = entityId == null || entityId === "" ? null : String(entityId);
    if (!id) return;
    this.drillContext = {
      dimension: this.displayedDimension,
      entityId: id,
      entityName: entityName == null ? id : String(entityName),
    };
  };

  exitDrill = () => {
    this.drillContext = null;
  };

  setConsolidated = (value) => {
    this.consolidated = Boolean(value);
    // Consolidated mode has no per-entity bars, so it cannot express a drill.
    if (this.consolidated) this.drillContext = null;
  };

  clearAll = () => {
    this.selectionByDimension = { bu: allSelection(), country: allSelection() };
    this.drillContext = null;
    this.yearRangeStart = null;
    this.yearRangeEnd = null;
    this.selectedYear = null;
    this.selectedYearIsManual = false;
    this.autoSelectedYearContextKey = "";
    this.autoAppliedRangeKey = "";
    this.consolidated = false;
  };

  // Which dimension the bars currently enumerate. A drill groups by the OTHER
  // dimension of the clicked entity, so every option list, color map, chart
  // title, legend, and row lookup keys off this and never off groupBy.
  get displayedDimension() {
    return this.drillContext
      ? otherDimension(this.drillContext.dimension)
      : this.groupBy;
  }

  get displayedSelection() {
    return this.selectionByDimension[this.displayedDimension];
  }

  // The company-wide surfaces (KPI strip, trend card, stacked totals card,
  // and with the strip the breakdown drawer) read tenant-wide endpoints that
  // take no entity filter, so they only tell the truth on the all-All
  // undrilled view. Everywhere else they step aside (open item 8, option a).
  get isTenantWideView() {
    return (
      this.drillContext === null &&
      this.selectionByDimension.bu.mode === "all" &&
      this.selectionByDimension.country.mode === "all"
    );
  }

  // `within` filter of the request the current data came from, or null when
  // the request carried none.
  get requestedWithin() {
    return this.lastRequest?.within ?? null;
  }

  get activeYears() {
    if (
      !Number.isFinite(this.yearRangeStart) ||
      !Number.isFinite(this.yearRangeEnd)
    ) {
      return [];
    }

    const start = Math.min(this.yearRangeStart, this.yearRangeEnd);
    const end = Math.max(this.yearRangeStart, this.yearRangeEnd);
    const years =
      this.availableYears.length > 0
        ? this.availableYears.filter((year) => year >= start && year <= end)
        : Array.from({ length: end - start + 1 }, (_, idx) => start + idx);

    return years.slice(0, MAX_YEAR_RANGE);
  }

  setData = (response, request = null, fingerprint = null, context = null) => {
    this.data = response ?? EMPTY_DATA;
    this.lastRequest = request;
    this.dataFingerprint = fingerprint;
    this.dataContext = context;
    this.loadError = false;
    this.hasLoadedOnce = true;
  };
  setLoadError = (value) => {
    this.loadError = Boolean(value);
  };
  setLoading = (value) => {
    this.loading = Boolean(value);
  };
  resetData = () => {
    this.data = EMPTY_DATA;
    this.lastRequest = null;
    this.dataFingerprint = null;
    this.dataContext = null;
    this.loadError = false;
    this.loading = false;
    this.hasLoadedOnce = false;
  };

  // Index rows by entityId for O(1) lookups in valueFor/totalFor. MobX
  // memoizes this getter — recomputes only when this.data changes.
  get rowsByEntityId() {
    const m = new Map();
    for (const row of this.data?.rows ?? []) {
      m.set(String(row.entityId), row);
    }
    return m;
  }

  // The dimension the STORED rows enumerate. While a refetch is pending the
  // ambient displayedDimension may already have moved (a group-by switch or a
  // drill), but the readers below always look up rows in this.data, so their
  // id normalization must follow the data, not the ambient state. Falls back
  // to displayedDimension before any data has landed.
  get dataDimension() {
    return this.dataContext?.displayedDimension ?? this.displayedDimension;
  }

  // Country picker ids are synthetic ("country:<key>"); the backend returns
  // rows keyed by Country.key (lowercase). Bridge the two spaces so components
  // can keep passing raw picker ids to the lookup helpers. Keyed off
  // dataDimension, NOT groupBy: during a business-unit-to-country drill
  // groupBy stays "bu" while the rows are country rows.
  lookupId = (id) => normalizeEntityId(this.dataDimension, id);

  // Chart cell readers. Returns null when the cell is missing or poisoned
  // by the backend (engine returned MISSING / MIXED_UNIT / UNDEFINED).
  valueFor = (entityId, year, scopeKey) => {
    const row = this.rowsByEntityId.get(this.lookupId(entityId));
    const cell = row?.years?.[String(year)];
    if (!cell || !Object.prototype.hasOwnProperty.call(cell, scopeKey)) {
      return null;
    }
    return cell[scopeKey];
  };

  totalFor = (entityId, year) => {
    const row = this.rowsByEntityId.get(this.lookupId(entityId));
    return row?.years?.[String(year)]?.total ?? null;
  };

  entityName = (entityId) => {
    const row = this.rowsByEntityId.get(this.lookupId(entityId));
    return row?.entityName ?? null;
  };

  // Sum across the given entities for a (year, scope). A null entity
  // contribution means "that entity has no disclosure submitted for this
  // year/scope", distinct from "we couldn't compute it". Across independent
  // entities we skip nulls and sum what we have. Only return null when nothing
  // exists at all, so the consolidated bar reflects the same data the
  // per-entity bars visualise (where null contributors render as zero-height
  // segments). Callers pass the panel's displayed entity list: it is the only
  // list that accounts for cross-filter omission.
  consolidatedValueFor = (year, scopeKey, entityIds = []) => {
    const ids = Array.isArray(entityIds) ? entityIds : [];
    if (ids.length === 0) return null;
    let sum = 0;
    let any = false;
    for (const id of ids) {
      const v = this.valueFor(id, year, scopeKey);
      if (v == null) continue;
      sum += v;
      any = true;
    }
    return any ? sum : null;
  };

  consolidatedTotalFor = (year, entityIds = []) => {
    let sum = 0;
    let any = false;
    for (const scope of SCOPE_KEYS) {
      const v = this.consolidatedValueFor(year, scope, entityIds);
      if (v == null) continue;
      sum += v;
      any = true;
    }
    return any ? sum : null;
  };

  get warnings() {
    return this.data?.warnings ?? [];
  }
}

export const dashboardEChartsStore = new DashboardEChartsStore();
export { DashboardEChartsStore };
export const E_CHARTS_BASE_YEAR = 2019;
export const E_CHARTS_MAX_YEAR_RANGE = MAX_YEAR_RANGE;
